import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContributionAccountField } from '@/components/giving/contribution-account-field';
import { RecordContributionSaveOptions } from '@/components/giving/record-contribution-save-options';
import {
  ContributionGiverFields,
  contributionGiverPayload,
  contributionGiverReady,
  type ContributionGiverKind,
} from '@/components/giving/contribution-giver-fields';
import {
  defaultOfferingDate,
  offeringCalendarDisabledDates,
  offeringCalendarMarkedDates,
  type Gathering,
} from '@/components/programs/gathering-schedule';
import type { ProgramDayDetail } from '@/components/programs/program-day-detail';
import { FormField } from '@/components/shared/form-field';
import { FundSelect } from '@/components/shared/fund-select';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { toast } from '@/components/ui/toast';
import { useGivingSettings } from '@/hooks/use-giving-settings';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { accountKindForMethod, CONTRIBUTION_METHODS } from '@/lib/contribution-methods';
import { resolveDefaultFund } from '@/lib/giving-default-funds';
import {
  loadRecordContributionPreferences,
  persistRecordContributionPreferences,
} from '@/lib/record-contribution-preferences';
import { resolveOfferingOccurrence } from '@/lib/resolve-offering-occurrence';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';

type GatheringKind = 'service' | 'program';

interface Fund {
  id: string;
  fundType: string;
  defaultCurrency: string;
}

export interface RecordOfferingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: GatheringKind;
  /** Service or program to show when the dialog opens. */
  initialProgramId?: string | null;
  /** Service date to show when the dialog opens. */
  initialServiceDate?: string | null;
}

const localeNs = (kind: GatheringKind) =>
  kind === 'service' ? 'services' : 'programOfferings';

export const RecordOfferingDialog = ({
  open,
  onOpenChange,
  kind,
  initialProgramId = null,
  initialServiceDate = null,
}: RecordOfferingDialogProps) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const { t: tPrograms } = useTranslation('programs');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const timezone = usePreferences((state) => state.timezone) ?? church?.defaultTimezone ?? 'UTC';
  const ns = localeNs(kind);

  const [fundId, setFundId] = useState<string | null>(null);
  const [programId, setProgramId] = useState<string | null>(initialProgramId);
  const [serviceDate, setServiceDate] = useState<string | null>(initialServiceDate);
  const [giverKind, setGiverKind] = useState<ContributionGiverKind>('internal');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [externalPartyId, setExternalPartyId] = useState<string | null>(null);
  const [giverName, setGiverName] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [method, setMethod] = useState('cash');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [savePreferences, setSavePreferences] = useState(() =>
    loadRecordContributionPreferences(church?.id),
  );
  const [formKey, setFormKey] = useState(0);

  const givingSettings = useGivingSettings();

  const programs = useQuery({
    queryKey: ['programs', kind],
    queryFn: () => api.get<{ data: Gathering[] }>(`/programs?kind=${kind}&pageSize=100`),
    enabled: open,
  });

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'active'],
    queryFn: () => api.get<{ data: Fund[] }>('/giving/funds?pageSize=100&activeOnly=true'),
    enabled: open,
  });

  const programRows = useMemo(() => programs.data?.data ?? [], [programs.data?.data]);
  const resolvedProgramId = programId ?? programRows[0]?.id ?? null;
  const selectedProgram = programRows.find((row) => row.id === resolvedProgramId) ?? null;

  const today = DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd');
  const offeringPastHorizon =
    DateTime.fromISO(today, { zone: timezone }).minus({ days: 366 }).toFormat('yyyy-MM-dd') ??
    today;
  const offeringFutureHorizon =
    DateTime.fromISO(today, { zone: timezone }).plus({ days: 366 }).toFormat('yyyy-MM-dd') ??
    today;

  const markedDates = useMemo(
    () => (selectedProgram ? offeringCalendarMarkedDates(selectedProgram, today) : []),
    [selectedProgram, today],
  );

  const resolvedServiceDate = useMemo(() => {
    if (serviceDate && (markedDates.length === 0 || markedDates.includes(serviceDate))) {
      return serviceDate;
    }
    return defaultOfferingDate(markedDates, today);
  }, [serviceDate, markedDates, today]);

  const disabledDates = useMemo(
    () =>
      markedDates.length === 0
        ? []
        : offeringCalendarDisabledDates(markedDates, offeringPastHorizon, offeringFutureHorizon),
    [markedDates, offeringPastHorizon, offeringFutureHorizon],
  );

  const dayDetails = useQuery({
    queryKey: ['programs', resolvedProgramId, 'day-details'],
    queryFn: () =>
      api.get<{ data: ProgramDayDetail[] }>(`/programs/${resolvedProgramId}/day-details`),
    enabled: open && Boolean(resolvedProgramId),
  });

  const noScheduleDates = Boolean(selectedProgram) && markedDates.length === 0;

  const programOptions = useMemo(
    () =>
      [...programRows]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((row) => ({ value: row.id, label: row.name })),
    [programRows],
  );

  const fundRows = useMemo(() => funds.data?.data ?? [], [funds.data?.data]);
  const rememberedFund = useMemo(() => {
    const stored = fundRows.find((row) => row.id === savePreferences.lastFundId);
    if (stored) return stored;
    const defaults = resolveDefaultFund(
      givingSettings.data,
      'offering',
      fundRows.map((row) => ({ ...row, isActive: true })),
    );
    return fundRows.find((row) => row.id === defaults.fundId) ?? null;
  }, [fundRows, savePreferences.lastFundId, givingSettings.data]);
  const selectedFund = fundRows.find((row) => row.id === (fundId ?? rememberedFund?.id)) ?? null;

  const applyFund = (nextFundId: string | null) => {
    setFundId(nextFundId);
    const next = { ...savePreferences, lastFundId: nextFundId };
    setSavePreferences(next);
    persistRecordContributionPreferences(church?.id, next);
  };

  const giverRequired = selectedFund?.fundType === 'tithe';
  const giver = { kind: giverKind, memberId, externalPartyId, giverName };
  const giverReady = contributionGiverReady({ ...giver, memberRequired: giverRequired });
  const currency = selectedFund?.defaultCurrency ?? church?.baseCurrency ?? 'GHS';
  const activeFundId = selectedFund?.id ?? null;

  const venueIdForDate = (date: string) => {
    const detail = (dayDetails.data?.data ?? []).find((row) => row.dates.includes(date));
    return detail?.venueId ?? selectedProgram?.venueId ?? null;
  };

  const record = useMutation({
    mutationFn: async () => {
      if (!resolvedProgramId || !resolvedServiceDate || !activeFundId || amountMinor === null) {
        throw new Error('OFFERING_INCOMPLETE');
      }
      const occurrenceId = await resolveOfferingOccurrence(
        resolvedProgramId,
        resolvedServiceDate,
        venueIdForDate(resolvedServiceDate),
      );
      return api.post('/giving/contributions', {
        fundId: activeFundId,
        ...contributionGiverPayload(giver),
        amountMinor,
        currency,
        contributionDate: resolvedServiceDate,
        occurrenceId,
        method,
        financialPositionItemId: accountKindForMethod(method) ? accountId : null,
        contributionType: selectedFund?.fundType,
      });
    },
    onSuccess: () => {
      toast.success(t('saved'));
      void queryClient.invalidateQueries({ queryKey: ['giving'] });
      void queryClient.invalidateQueries({ queryKey: ['occurrences'] });
      if (savePreferences.addAnother) {
        setMemberId(null);
        setExternalPartyId(null);
        setGiverName('');
        setAmountMinor(null);
        setMethod('cash');
        setAccountId(null);
        setFormKey((current) => current + 1);
        return;
      }
      onOpenChange(false);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible" size="xl">
        <form
          key={formKey}
          className="min-h-0"
          onSubmit={(event) => {
            event.preventDefault();
            if (!activeFundId || amountMinor === null || !resolvedProgramId || !resolvedServiceDate) return;
            if (!giverReady) return;
            record.mutate();
          }}
        >
          <DialogBody
            className="max-h-[calc(92dvh-3rem)] overflow-visible"
            contentClassName="flex flex-col gap-6"
          >
            <DialogHeader>
              <DialogTitle>{t(`${ns}.recordOffering`)}</DialogTitle>
            </DialogHeader>
            <div className="grid items-start gap-x-10 gap-y-6 sm:grid-cols-2">
              <div className="flex min-w-0 flex-col gap-6">
                <FormField label={t('form.fund')}>
                  {(field) => (
                    <FundSelect
                      id={field.id}
                      value={activeFundId}
                      onChange={(next) => applyFund(next)}
                      popoverPortal="dialog"
                    />
                  )}
                </FormField>
                <FormField label={t(`${ns}.program`)}>
                  {(field) => (
                    <Listbox
                      id={field.id}
                      value={resolvedProgramId ?? ''}
                      onChange={(value) => {
                        setProgramId(value);
                        setServiceDate(null);
                      }}
                      options={programOptions}
                      searchable
                      searchPlaceholder={tCommon('search.placeholder')}
                      emptyMessage={tCommon('search.noResults')}
                      popoverPortal="dialog"
                      disabled={programOptions.length === 0}
                    />
                  )}
                </FormField>
                <FormField label={t(`${ns}.date`)} hint={resolvedProgramId && !noScheduleDates ? t(`${ns}.recordCalendarHint`) : undefined}>
                  {() =>
                    !resolvedProgramId || programs.isLoading ? (
                      <p className="text-body-sm text-fg-secondary">
                        {programs.isLoading ? tCommon('state.loading') : t(`${ns}.chooseDay`)}
                      </p>
                    ) : noScheduleDates ? (
                      <p className="text-body-sm text-fg-secondary">{t(`${ns}.noDaysAvailable`)}</p>
                    ) : (
                      <Calendar
                        value={resolvedServiceDate}
                        onChange={setServiceDate}
                        min={offeringPastHorizon}
                        max={offeringFutureHorizon}
                        disabledDates={disabledDates}
                        markedDates={markedDates}
                        popoverPortal="dialog"
                        previousMonthLabel={tPrograms('calendar.previousMonth')}
                        nextMonthLabel={tPrograms('calendar.nextMonth')}
                        className="w-full"
                      />
                    )
                  }
                </FormField>
              </div>
              <div className="flex min-w-0 flex-col gap-6">
                <FormField label={t('form.amount')}>
                  {(field) => (
                    <MoneyInput
                      id={field.id}
                      value={amountMinor}
                      onChange={setAmountMinor}
                      currency={currency}
                    />
                  )}
                </FormField>
                <ContributionGiverFields
                  open={open}
                  kind={giverKind}
                  onKindChange={(next) => {
                    setGiverKind(next);
                    setMemberId(null);
                    setExternalPartyId(null);
                    setGiverName('');
                  }}
                  memberId={memberId}
                  onMemberIdChange={setMemberId}
                  externalPartyId={externalPartyId}
                  onExternalPartyChange={setExternalPartyId}
                  giverName={giverName}
                  onGiverNameChange={setGiverName}
                  memberOptional={!giverRequired}
                />
                <FormField label={t('form.method')}>
                  {(field) => (
                    <Listbox
                      id={field.id}
                      value={method}
                      onChange={(value) => {
                        setMethod(value ?? 'cash');
                        setAccountId(null);
                      }}
                      options={CONTRIBUTION_METHODS.map((value) => ({
                        value,
                        label: t(`methods.${value}` as 'methods.cash'),
                      }))}
                      popoverPortal="dialog"
                    />
                  )}
                </FormField>
                <ContributionAccountField
                  open={open}
                  method={method}
                  currency={currency}
                  value={accountId}
                  onChange={setAccountId}
                />
                <RecordContributionSaveOptions
                  addAnother={savePreferences.addAnother}
                  onAddAnotherChange={(addAnother) => {
                    const next = { ...savePreferences, addAnother };
                    setSavePreferences(next);
                    persistRecordContributionPreferences(church?.id, next);
                  }}
                  disabled={record.isPending}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={record.isPending}
                disabled={
                  !activeFundId ||
                  amountMinor === null ||
                  !resolvedProgramId ||
                  !resolvedServiceDate ||
                  noScheduleDates ||
                  !giverReady ||
                  Boolean(accountKindForMethod(method) && !accountId)
                }
              >
                {t('form.save')}
              </Button>
            </DialogFooter>
          </DialogBody>
        </form>
      </DialogContent>
    </Dialog>
  );
};
